import React, {useEffect, useRef, useState} from 'react';
import {observer} from 'mobx-react';
import {buttonMultilineShipmentsStore} from '../button-multiline-shipments/button-multiline-shipments-store';
import {ICartLineItem} from '../../../../i';
import {importCsvAddresses} from './parse-csv-addresses';
import {CsvFileReadError, readFileAsText} from './read-csv-file';

type Props = {
    cartItem: ICartLineItem;
    modalId: string;
};

type ImportStatusType = 'importing' | 'success' | 'warning' | 'failure';

type ImportStatus = {
    type: ImportStatusType;
    message: string;
};

const STATUS_COLOR: Record<ImportStatusType, string> = {
    importing: '#171717',
    success: '#0d5c2e',
    warning: '#7a5b00',
    failure: '#9b1c1c',
};

const ButtonImportCsv = observer(({cartItem, modalId}: Props) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const importingRef = useRef(false);
    const mountedRef = useRef(true);
    const importSessionRef = useRef(0);
    const [importing, setImporting] = useState(false);
    const [status, setStatus] = useState<ImportStatus | null>(null);

    const helpId = `csv-import-help-${cartItem.id}`;
    const statusId = `csv-import-status-${cartItem.id}`;
    const describedBy = status ? `${helpId} ${statusId}` : helpId;

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        const modal = document.getElementById(modalId);

        if (!modal) {
            return;
        }

        const endImportSession = () => {
            importSessionRef.current += 1;
            importingRef.current = false;
            setImporting(false);
            setStatus(null);

            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        };

        const syncModalVisibility = () => {
            if (modal.style.display !== 'block') {
                endImportSession();
            }
        };

        syncModalVisibility();

        const observer = new MutationObserver(syncModalVisibility);

        observer.observe(modal, {attributes: true, attributeFilter: ['style']});

        return () => observer.disconnect();
    }, [modalId]);

    const handleClick = () => {
        if (importingRef.current) {
            return;
        }

        fileInputRef.current?.click();
    };

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target;
        const file = input.files?.[0];

        if (!file) {
            return;
        }

        if (importingRef.current) {
            input.value = '';
            return;
        }

        const session = importSessionRef.current;

        importingRef.current = true;

        if (mountedRef.current) {
            setImporting(true);
            setStatus({type: 'importing', message: 'Importing…'});
        }

        const isCurrentSession = () => mountedRef.current && session === importSessionRef.current;

        try {
            const text = await readFileAsText(file);
            const result = importCsvAddresses(cartItem.address, text, cartItem.qty);

            if (!isCurrentSession()) {
                return;
            }

            if (!result.ok) {
                setStatus({type: 'failure', message: result.error});
                return;
            }

            cartItem.address = result.addresses;

            if (result.warning) {
                setStatus({type: 'warning', message: result.warning});
            } else {
                setStatus({type: 'success', message: 'Addresses imported.'});
            }

            buttonMultilineShipmentsStore.expandAll(cartItem);
        } catch (err) {
            if (!isCurrentSession()) {
                return;
            }

            const message = err instanceof CsvFileReadError ? err.message : 'Could not parse the CSV file.';

            setStatus({type: 'failure', message});
        } finally {
            input.value = '';

            if (session === importSessionRef.current) {
                importingRef.current = false;

                if (mountedRef.current) {
                    setImporting(false);
                }
            }
        }
    };

    return (
        <div style={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', maxWidth: '100%'}}>
            <button
                className="btn-checkout btn-checkout-white btn-shipping-white"
                type="button"
                aria-label="Import shipping addresses"
                aria-describedby={describedBy}
                aria-busy={importing}
                disabled={importing}
                onClick={handleClick}
            >
                Import
            </button>
            {status && (
                <span
                    id={statusId}
                    role="status"
                    aria-live="polite"
                    style={{color: STATUS_COLOR[status.type], fontSize: '13px', lineHeight: 1.3}}
                >
                    {status.message}
                </span>
            )}
            <span
                id={helpId}
                className="sr-only"
            >
                Select a CSV file containing shipping addresses
            </span>
            <input
                type="file"
                accept=".csv"
                tabIndex={-1}
                aria-hidden="true"
                ref={fileInputRef}
                style={{display: 'none'}}
                disabled={importing}
                onChange={handleFileSelect}
            />
        </div>
    );
});

export default ButtonImportCsv;
